import type { ItemDetail, Member } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { titleOf } from "../../lib/titles.ts";
import { RenameIcon } from "../../ui/icons.tsx";
import { IdCode } from "../../ui/misc.tsx";
import { iconButtonClass } from "../../ui/styles.ts";
import { TitleField } from "../../ui/TitleField.tsx";
import { KindIcon } from "../items/icons.tsx";
import { VisibilityMark } from "./visibility.tsx";

/**
 * An item's heading, as its page and a quick look at it show it: what it is (an email, a PDF…) before its title, then
 * who sees it and its id (as on every page with an id). From outside its space, just what it is and its title.
 */
export function ItemHeading({
  item,
  members,
  look = false,
  outside = false,
  rename,
}: {
  item: ItemDetail;
  members: readonly Member[] | undefined;
  /** In a quick look (smaller), not on its page. */
  look?: boolean;
  outside?: boolean;
  /** Renaming it: its title as a field (Enter keeps it, Escape or leaving it unchanged doesn't). Given, there's a
   *  button to start (`onRename(undefined)`) beside the title. `field`: its input's id; `original`: what it came as,
   *  what an emptied field goes back to. */
  rename?: {
    editing: boolean;
    onRename: (title: string | null | undefined) => void;
    field?: string;
    original?: string | null | undefined;
  };
}) {
  return (
    <div class="flex items-center gap-2">
      <span class="shrink-0">
        <KindIcon item={item} />
      </span>
      {rename?.editing ? (
        <TitleField title={item.title ?? ""} id={rename.field} original={rename.original} onDone={rename.onRename} />
      ) : look ? (
        <h2 class="line-clamp-2 min-w-0 font-medium [overflow-wrap:anywhere] text-zinc-900">{titleOf(item)}</h2>
      ) : (
        <h1 class="min-w-0 text-xl font-semibold break-words text-zinc-900">{titleOf(item)}</h1>
      )}
      {rename && !rename.editing ? (
        <button
          type="button"
          class={`${iconButtonClass("sm")} shrink-0 text-zinc-500`}
          title={t("item.rename")}
          onClick={() => rename.onRename(undefined)}
        >
          <RenameIcon />
          <span class="sr-only">{t("item.rename")}</span>
        </button>
      ) : null}
      {outside ? null : <VisibilityMark item={item} members={members} size={look ? 16 : 18} />}
      {/* While renaming, the field has its room. */}
      {outside || rename?.editing ? null : <IdCode id={item.id} class="ml-1" />}
    </div>
  );
}
